import React, { useState, useEffect } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading FAQs...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <HelpCircle className="text-red-600" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Frequently Asked <span className="text-red-600">Questions</span>
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl">
            Find answers to common questions about our training programs, certifications, and policies.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={faq.id} value={`item-${index}`} className="border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold hover:text-red-600 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center text-gray-600 py-8">
              <p>No FAQs available at the moment. Please check back later.</p>
            </div>
          )}
        </div>
      </section>

      {/* Additional FAQ Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            More Questions?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-red-200 transition-all">
              <CardHeader>
                <CardTitle className="text-xl">Course Details</CardTitle>
                <CardDescription>
                  Learn about our training curriculum, class structure, and certification process.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/services">
                  <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-50">
                    View Services
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-200 transition-all">
              <CardHeader>
                <CardTitle className="text-xl">Policies</CardTitle>
                <CardDescription>
                  Review our cancellation, reschedule, and refund policies before booking.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/policy">
                  <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-50">
                    Read Policies
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-200 transition-all">
              <CardHeader>
                <CardTitle className="text-xl">Contact Us</CardTitle>
                <CardDescription>
                  Can't find your answer? Reach out to our team directly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-red-600 text-red-600 hover:bg-red-50">
                    Get in Touch
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
